import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector, shallowEqual } from "react-redux";
import { Dropdown } from "semantic-ui-react";
import { formatElements } from "./parseText";
import make_call from "./Request";
import Loading from "./Loading";
import SlideBackground from "assets/images/UI/slide_background.svg";

/**
 * Props:
 * - slideData (Object)         => Saved data from the parent (e.g. { selectedFactor, message })
 * - updateSlideData (Function) => Parent callback to save updated slide data
 * - outcomeQ (Object)          => Outcome questions data (similar to the original code)
 * - componentLoading (Boolean) => Whether the component is in a "loading" state at the parent level
 */
const ConceptExplanationSlide = ({
  slideData,
  updateSlideData,
  outcomeQ,
  componentLoading,
}) => {
  // Pull coreData from Redux (same as original code)
  const coreData = useSelector((state) => state.audit?.core_data, shallowEqual);

  // State for dropdown options, selected factor, and AI explanation
  const [factorDropdown, setFactorDropdown] = useState([]);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(false);

  /**
   * 1. Build the factor dropdown options as in the original code,
   *    plus outcomeQ if desired.
   */
  useEffect(() => {
    if (coreData) {
      let options = [];

      // Loop through all standard factors
      coreData.questions.dimensions.forEach((dimension, i) => {
        dimension.factors.forEach((factor) => {
          options.push({
            key: factor.id + factor.title,
            text: factor.title,
            value: { ...factor, dimension: i, type: "factor" },
          });
        });
      });
      // Add outcome factors/questions
      coreData.outcomeQuestions.questions.forEach((q) => {
        options.push({
          key: q.id + q.title,
          text: q.name,
          value: { ...q, type: "outcome" },
        });
      });

      setFactorDropdown(options);
      // If nothing saved yet, default to the first item
      if (!slideData?.selectedFactor) {
        setSelectedFactor(options[0].value);
      }
    }
  }, [coreData, outcomeQ, slideData]);

  /**
   * 2. When we receive slideData from the parent, restore selected factor and explanation.
   */
  useEffect(() => {
    if (slideData) {
      if (slideData.selectedFactor) {
        setSelectedFactor(slideData.selectedFactor);
      }
      if (slideData.message) {
        setExplanation(slideData.message);
      }
    }
  }, [slideData]);

  /**
   * 3. Whenever selectedFactor or explanation changes (and we aren't currently loading an AI response),
   *    update the parent-level slide data so it stays in sync.
   */
  useEffect(() => {
    if (!loading && selectedFactor) {
      const newSlideData = {
        ...slideData,
        slide_template_id: 7,
        selectedFactor: selectedFactor,
        // If user chooses "All Factors", you can make a special title or fallback
        title:
          selectedFactor && selectedFactor !== "All Factors"
            ? `What is ${selectedFactor.title || selectedFactor.text || selectedFactor.name}?`
            : "Concept Explanation",
        message: explanation,
      };

      if (JSON.stringify(newSlideData) !== JSON.stringify(slideData)) {
        updateSlideData(newSlideData);
      }
    }
    // eslint-disable-next-line
  }, [selectedFactor, explanation, loading]);

  /**
   * 4. Handle factor dropdown changes
   */
  const handleFactorChange = (e, { value }) => {
    setSelectedFactor(value);
  };

  /**
   * 5. Generate Explanation from AI
   */
  const handleGenerateExplanation = async () => {
    if (!selectedFactor || selectedFactor === "All Factors" || loading) return;

    setLoading(true);
    setExplanation("");

    // Attempt to grab a "name" for the concept from the factor/outcome object
    const conceptName =
      selectedFactor.title || selectedFactor.text || selectedFactor.name || "";

      const prompt = `
      Explain what "${conceptName}" is in a short, concise manner (2-3 sentences).
      Draw from academic sources, and in the context of organizational culture.
      Provide a brief definition and give an inspirational quote and source it about "${conceptName}". 
      Don't title the definition and inspirational quote, simply Format your response as follows add bolded text to highlight an important part:

       A brief definition of the concept.
       > An inspirational quote about the concept.
    `;

    // Stream tokens from AI
    await make_call(prompt, (chunk) => {
      setExplanation((prev) => prev + chunk);
    });

    setLoading(false);
  };

  // 6. If the parent is in a loading state, show spinner
  if (componentLoading) {
    return <Loading />;
  }

  return (
    <Container>
      <Header>Concept Explanation Slide</Header>

      <Controls>
        <Dropdown
          placeholder="Select Factor or Outcome"
          selection
          options={factorDropdown}
          onChange={handleFactorChange}
          value={selectedFactor}
        />

        <Button onClick={handleGenerateExplanation}>
          Generate Explanation
        </Button>
      </Controls>

      <SlidePreview>
        <SlideTitle>
          {selectedFactor && selectedFactor !== "All Factors"
            ? `What is ${
                selectedFactor.title ||
                selectedFactor.text ||
                selectedFactor.name
              }?`
            : "Concept Explanation"}
        </SlideTitle>

        <SlideDescription>
          {loading && !explanation ? (
            <Loading />
          ) : (
            formatElements(
              explanation || "Your explanation will appear here."
            )
          )}
        </SlideDescription>

        <SlideFooter>innerlogic</SlideFooter>
      </SlidePreview>
    </Container>
  );
};

export default ConceptExplanationSlide;

/* -------------- Styled Components -------------- */

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  animation: ${fadeIn} 1s ease-out forwards;
  padding: 20px;
  opacity: 0; /* starts from 0 to allow fade-in */
  display: flex;
  flex-direction: column;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
font-size: 20px;
font-weight: 600;
margin-bottom:10px;
color:#2A3039;
`;

const Controls = styled.div`
margin-bottom:20px;
    display:flex;
    padding:20px;
    background-color:#f9fbfd;
    width:100%;
`;

const Button = styled.button`
  background-color: #2d70e2;
  color: white;
  padding: 10px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  border: none;
  transition: background 0.2s ease;
  margin-left:10px;
  &:hover {
    background-color: #2252a8;
  }
`;

const SlidePreview = styled.div`
  position: relative;
  width: 100%;
  min-height: 500px;
  border-radius: 10px;
  padding: 30px;
  background: linear-gradient(
    to right,
    #ecf5f9,
    #e9f4f8,
    #e5f2f6,
    #e1f1f4,
    #ddeff2,
    #d9eef0,
    #d5edef,
    #d1ebed,
    #ceeaeb,
    #cae8e9
  );
  overflow: hidden;
`;

const SlideBackgroundImage = styled.img`
  position: absolute;
  bottom: -50%;
  right: 0;
  opacity: 0.3;
  max-width: 60%;
`;

const SlideTitle = styled.h3`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333333;
  font-family: "Raleway", sans-serif;
`;

const SlideDescription = styled.div`
  font-size: 24px;
  line-height: 1.4;
`;

const SlideFooter = styled.div`
  position: absolute;
  bottom: 0;
  width: calc(100% - 60px);
  padding: 10px 0;
  font-size: 12px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  color: #777;
`;
